import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { encryptionService, encryptionMigrationService, SENSITIVE_FIELDS } from '../utils/encryption.js';
import { logger } from '../utils/logger.js';

class FirebaseService {
  constructor() {
    this.db = null;
    this.listeners = new Map();
  }

  initialize(db) {
    this.db = db;
  }

  async createDocument(collectionPath, data, customId = null) {
    try {
      const timestamp = serverTimestamp();
      const documentData = {
        ...data,
        createdAt: timestamp,
        updatedAt: timestamp
      };

      if (customId) {
        const docRef = doc(this.db, collectionPath, customId);
        await setDoc(docRef, documentData);
        return { id: customId, ...documentData };
      } else {
        const docRef = await addDoc(collection(this.db, collectionPath), documentData);
        return { id: docRef.id, ...documentData };
      }
    } catch (error) {
      logger.error('Error creating document:', error);
      throw error;
    }
  }

  async getDocument(collectionPath, documentId) {
    try {
      const docRef = doc(this.db, collectionPath, documentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        return null;
      }
    } catch (error) {
      logger.error('Error getting document:', error);
      throw error;
    }
  }

  async updateDocument(collectionPath, documentId, updates) {
    try {
      const docRef = doc(this.db, collectionPath, documentId);

      // Replace null values with deleteField() to remove fields from Firestore
      const updateData = Object.entries(updates).reduce((acc, [key, value]) => {
        acc[key] = value === null ? deleteField() : value;
        return acc;
      }, {});

      updateData.updatedAt = serverTimestamp();

      await updateDoc(docRef, updateData);
      return { id: documentId, ...updateData };
    } catch (error) {
      logger.error('Error updating document:', error);
      throw error;
    }
  }

  async deleteDocument(collectionPath, documentId) {
    try {
      const docRef = doc(this.db, collectionPath, documentId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      logger.error('Error deleting document:', error);
      throw error;
    }
  }

  async getCollection(collectionPath, constraints = []) {
    try {
      const collectionRef = collection(this.db, collectionPath);
      let q = collectionRef;

      if (constraints.length > 0) {
        q = query(collectionRef, ...constraints);
      }

      const querySnapshot = await getDocs(q);
      const documents = [];
      
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });

      return documents;
    } catch (error) {
      logger.error('Error getting collection:', error);
      throw error;
    }
  }

  async createUser(userData) {
    try {
      const userRef = doc(this.db, 'users', userData.uid);
      await setDoc(userRef, {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active'
      });
      return userData;
    } catch (error) {
      logger.error('Error creating user:', error);
      throw error;
    }
  }

  async getUser(uid) {
    return this.getDocument('users', uid);
  }

  async updateUser(uid, updates) {
    return this.updateDocument('users', uid, updates);
  }

  async getUserByEmail(email) {
    try {
      const constraints = [where('email', '==', email), limit(1)];
      const users = await this.getCollection('users', constraints);
      return users.length > 0 ? users[0] : null;
    } catch (error) {
      logger.error('Error getting user by email:', error);
      throw error;
    }
  }

  async createAgency(agencyData, ownerId) {
    try {
      // Use ownerId as agencyId for simplicity
      const agencyId = ownerId;
      const agency = await this.createDocument('agencies', {
        ...agencyData,
        id: agencyId,
        ownerId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        subscription: {
          plan: 'free',
          status: 'active',
          startDate: serverTimestamp(),
          maxClients: 5,
          features: ['basic_reports', 'client_management']
        },
        settings: {
          defaultTimezone: 'America/Sao_Paulo',
          currency: 'BRL',
          reportingFrequency: 'monthly'
        }
      }, agencyId);

      // Update user with agencyId (role já foi setado no create do user)
      await this.updateUser(ownerId, { agencyId });

      return agency;
    } catch (error) {
      logger.error('Error creating agency:', error);
      throw error;
    }
  }

  async getAgency(agencyId) {
    return this.getDocument('agencies', agencyId);
  }

  async updateAgency(agencyId, updates) {
    return this.updateDocument('agencies', agencyId, updates);
  }

  async getAgencyByOwnerId(ownerId) {
    try {
      const constraints = [where('ownerId', '==', ownerId), limit(1)];
      const agencies = await this.getCollection('agencies', constraints);
      return agencies.length > 0 ? agencies[0] : null;
    } catch (error) {
      logger.error('Error getting agency by owner:', error);
      throw error;
    }
  }

  async createClient(agencyId, clientData, createUserAccount = false) {
    try {
      const clientId = this.generateId();
      const clientPath = `agencies/${agencyId}/clients`;

      // Criptografa campos sensíveis via Cloud Function (v2 AES-256-GCM)
      let encryptedData = clientData;
      try {
        const result = await encryptionMigrationService.encryptViaCloudFunction(clientData, SENSITIVE_FIELDS.CLIENT_DATA);
        if (result.success && result.data) {
          encryptedData = result.data;
        }
      } catch (encryptError) {
        logger.warn('[SECURITY] Cloud encryption unavailable, storing without encryption:', encryptError.message);
      }

      // Data de início de contrato: usa startDate do formulário, contractStartDate ou data atual
      const contractStartDate = clientData.startDate || clientData.contractStartDate || new Date().toISOString().split('T')[0];

      const client = await this.createDocument(clientPath, {
        ...encryptedData,
        id: clientId,
        agencyId,
        contractStartDate, // Data de início do contrato para métricas históricas
        accessPermissions: {
          canViewReports: true,
          canViewCampaigns: true,
          canViewMetrics: true,
          canEditProfile: false
        },
        loginCredentials: {
          isLoginEnabled: createUserAccount,
          lastLogin: null,
          loginCount: 0
        },
        billingInfo: {
          monthlyBudget: 0,
          totalSpent: 0,
          currency: 'BRL',
          paymentMethod: ''
        },
        metaAdAccountIds: clientData.metaAdAccountIds || [],
        googleAdAccountIds: clientData.googleAdAccountIds || []
      }, clientId);

      return client;
    } catch (error) {
      logger.error('Error creating client:', error);
      throw error;
    }
  }

  async getClient(agencyId, clientId) {
    return this.getDocument(`agencies/${agencyId}/clients`, clientId);
  }

  async updateClient(agencyId, clientId, updates) {
    // Mapeia startDate para contractStartDate se fornecido
    if (updates.startDate && !updates.contractStartDate) {
      updates.contractStartDate = updates.startDate;
    }
    // Criptografa campos sensíveis via Cloud Function (v2 AES-256-GCM)
    let encryptedUpdates = updates;
    try {
      const result = await encryptionMigrationService.encryptViaCloudFunction(updates, SENSITIVE_FIELDS.CLIENT_DATA);
      if (result.success && result.data) {
        encryptedUpdates = result.data;
      }
    } catch (encryptError) {
      logger.warn('[SECURITY] Cloud encryption unavailable for update:', encryptError.message);
    }
    return this.updateDocument(`agencies/${agencyId}/clients`, clientId, encryptedUpdates);
  }

  async deleteClient(agencyId, clientId) {
    return this.deleteDocument(`agencies/${agencyId}/clients`, clientId);
  }

  async getAgencyClients(agencyId, statusFilter = null) {
    try {
      const constraints = [];
      
      if (statusFilter) {
        constraints.push(where('status', '==', statusFilter));
      }
      
      constraints.push(orderBy('createdAt', 'desc'));
      
      return this.getCollection(`agencies/${agencyId}/clients`, constraints);
    } catch (error) {
      logger.error('Error getting agency clients:', error);
      throw error;
    }
  }

  async createCampaign(agencyId, clientId, campaignData) {
    try {
      const campaignId = this.generateId();
      const campaignPath = `agencies/${agencyId}/clients/${clientId}/campaigns`;
      
      return this.createDocument(campaignPath, {
        ...campaignData,
        id: campaignId,
        agencyId,
        clientId,
        metrics: {
          impressions: 0,
          clicks: 0,
          conversions: 0,
          spend: 0,
          cpm: 0,
          cpc: 0,
          ctr: 0,
          roas: 0,
          lastUpdated: serverTimestamp()
        }
      }, campaignId);
    } catch (error) {
      logger.error('Error creating campaign:', error);
      throw error;
    }
  }

  async getClientCampaigns(agencyId, clientId) {
    try {
      const constraints = [orderBy('createdAt', 'desc')];
      return this.getCollection(`agencies/${agencyId}/clients/${clientId}/campaigns`, constraints);
    } catch (error) {
      logger.error('Error getting client campaigns:', error);
      throw error;
    }
  }

  subscribeToCollection(collectionPath, callback, constraints = []) {
    try {
      const collectionRef = collection(this.db, collectionPath);
      let q = collectionRef;

      if (constraints.length > 0) {
        q = query(collectionRef, ...constraints);
      }

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const documents = [];
        querySnapshot.forEach((doc) => {
          documents.push({ id: doc.id, ...doc.data() });
        });
        callback(documents);
      });

      // Store listener for cleanup
      const listenerId = this.generateId();
      this.listeners.set(listenerId, unsubscribe);
      
      return listenerId;
    } catch (error) {
      logger.error('Error subscribing to collection:', error);
      throw error;
    }
  }

  subscribeToDocument(documentPath, callback) {
    try {
      const docRef = doc(this.db, documentPath);
      
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          callback({ id: docSnap.id, ...docSnap.data() });
        } else {
          callback(null);
        }
      });

      // Store listener for cleanup
      const listenerId = this.generateId();
      this.listeners.set(listenerId, unsubscribe);
      
      return listenerId;
    } catch (error) {
      logger.error('Error subscribing to document:', error);
      throw error;
    }
  }

  unsubscribe(listenerId) {
    const unsubscribe = this.listeners.get(listenerId);
    if (unsubscribe) {
      unsubscribe();
      this.listeners.delete(listenerId);
    }
  }

  unsubscribeAll() {
    this.listeners.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.listeners.clear();
  }

  async executeBatch(operations) {
    try {
      const batch = writeBatch(this.db);
      
      operations.forEach(({ type, path, data, id }) => {
        const docRef = id ? doc(this.db, path, id) : doc(collection(this.db, path));
        
        switch (type) {
          case 'create':
          case 'set':
            batch.set(docRef, { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
            break;
          case 'update':
            batch.update(docRef, { ...data, updatedAt: serverTimestamp() });
            break;
          case 'delete':
            batch.delete(docRef);
            break;
        }
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      logger.error('Error executing batch:', error);
      throw error;
    }
  }

  generateId() {
    return 'id_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
  }
}

export const firebaseService = new FirebaseService();